import React, { useState, useEffect } from 'react';
import { Claim, client as apiClient } from '../api/claims';
import Modal from './ModalTrue';
import './LitigationFriendModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Local InfoRow component
const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  const hasValue = value !== undefined && value !== null && value !== '';
  return (
    <div style={{ marginBottom: '8px' }}>
      <strong>{label}:</strong>{' '}
      {hasValue ? value : 'N/A'}
    </div>
  );
};

type LitigationFriendFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  addressLine1: string;
  addressLine2: string;
  town: string;
  county: string;
  postcode: string;
  dateObtained: string;
  methodObtained: string;
  proofOnFile: boolean;
  role: 'executor' | 'administrator' | 'litigationFriend' | '';
  relationshipToClient: string;
  deathCertificateStatus: 'received' | 'outstanding' | '';
  willStatus: 'received' | 'outstanding' | 'na' | '';
  grantOfProbateStatus: 'received' | 'outstanding' | 'na' | '';
  letterOfAdministrationStatus: 'received' | 'outstanding' | 'na' | '';
  willExists: boolean;
  dateOfDeath: string;
};

const blankForm = (): LitigationFriendFormData => ({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  dateOfBirth: '',
  addressLine1: '',
  addressLine2: '',
  town: '',
  county: '',
  postcode: '',
  dateObtained: '',
  methodObtained: '',
  proofOnFile: false,
  role: '',
  relationshipToClient: '',
  deathCertificateStatus: '',
  willStatus: '',
  grantOfProbateStatus: '',
  letterOfAdministrationStatus: '',
  willExists: false,
  dateOfDeath: '',
});

const populateForm = (details: any): LitigationFriendFormData => ({
  firstName: details?.firstName || '',
  lastName: details?.lastName || '',
  email: details?.email || '',
  phoneNumber: details?.phoneNumber || '',
  dateOfBirth: details?.dateOfBirth || '',
  addressLine1: details?.addressLine1 || '',
  addressLine2: details?.addressLine2 || '',
  town: details?.town || '',
  county: details?.county || '',
  postcode: details?.postcode || '',
  dateObtained: details?.dateObtained || '',
  methodObtained: details?.methodObtained || '',
  proofOnFile: details?.proofOnFile || false,
  role: details?.role || '',
  relationshipToClient: details?.relationshipToClient || '',
  deathCertificateStatus: details?.deathCertificateStatus || '',
  willStatus: details?.willStatus || '',
  grantOfProbateStatus: details?.grantOfProbateStatus || '',
  letterOfAdministrationStatus: details?.letterOfAdministrationStatus || '',
  // Legacy records predate the explicit willExists flag: derive it from willStatus.
  willExists: details?.willExists ?? (details?.willStatus === 'received' || details?.willStatus === 'outstanding'),
  dateOfDeath: details?.dateOfDeath || '',
});

interface LitigationFriendModalProps {
  isOpen: boolean;
  onClose: () => void;
  claim: Claim | null;
  onSaveSuccess: (updatedClaim: Claim) => void;
  onMessage: (msg: string) => void;
}

const LitigationFriendModal: React.FC<LitigationFriendModalProps> = ({
  isOpen,
  onClose,
  claim,
  onSaveSuccess,
  onMessage
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<LitigationFriendFormData>(blankForm());
  const [saving, setSaving] = useState(false);
  const [sendingDocRequest, setSendingDocRequest] = useState(false);
  const [sendingSigningLink, setSendingSigningLink] = useState(false);
  const [uploadingKind, setUploadingKind] = useState<string | null>(null);
  const [sendingIdRequest, setSendingIdRequest] = useState(false);
  const [executorIdStatus, setExecutorIdStatus] = useState<string | null>(null);

  // Populate form when modal opens
  useEffect(() => {
    if (isOpen && claim) {
      if (claim.litigationFriendDetails) {
        setFormData(populateForm(claim.litigationFriendDetails));
        setIsEditMode(false);
      } else {
        setFormData(blankForm());
        setIsEditMode(true);
      }
    }
  }, [isOpen, claim]);

  // Fetch executor ID verification status
  useEffect(() => {
    const executorClientId = claim?.litigationFriendDetails?.executorClientId;
    if (isOpen && executorClientId) {
      fetch(`${API_BASE_URL}/ui/photo-id/client/${executorClientId}/verification`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(res => res.json())
        .then(data => {
          setExecutorIdStatus(data.verificationStatus || null);
        })
        .catch(() => setExecutorIdStatus(null));
    } else {
      setExecutorIdStatus(null);
    }
  }, [isOpen, claim]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-active');
    } else {
      document.body.classList.remove('modal-active');
    }
    return () => document.body.classList.remove('modal-active');
  }, [isOpen]);

  if (!isOpen || !claim) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiClient.post<{ claim: Claim }>(`/ui/claims/${claim.id}/litigation-friend`, formData);
      const updatedClaim = response.data.claim;
      onSaveSuccess(updatedClaim);
      setIsEditMode(false);
      onMessage('Litigation friend details saved successfully');
    } catch (err: any) {
      onMessage(`Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setFormData(populateForm(claim.litigationFriendDetails));
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (!claim.litigationFriendDetails) {
      onClose();
    } else {
      setIsEditMode(false);
    }
  };

  const lfd: any = claim.litigationFriendDetails || {};
  // Legacy records predate the explicit willExists flag: derive it from willStatus.
  const willExists: boolean = lfd.willExists ?? (lfd.willStatus === 'received' || lfd.willStatus === 'outstanding');

  const handleUploadDoc = async (kind: string, file: File) => {
    setUploadingKind(kind);
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(`${API_BASE_URL}/ui/claims/${claim.id}/deceased-documents/${kind}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body,
      });
      const result = await response.json();
      if (response.ok) {
        onMessage('Document uploaded');
        onSaveSuccess(result.claim);
      } else {
        onMessage(`Error: ${result.error || 'Upload failed'}`);
      }
    } catch (err: any) {
      onMessage(`Error: ${err.message}`);
    } finally {
      setUploadingKind(null);
    }
  };

  const handleViewDoc = async (kind: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ui/claims/${claim.id}/deceased-documents/${kind}/url`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      const result = await response.json();
      if (response.ok && result.url) {
        window.open(result.url, '_blank');
      } else {
        onMessage(`Error: ${result.error || 'Failed to open document'}`);
      }
    } catch (err: any) {
      onMessage(`Error: ${err.message}`);
    }
  };

  const handleSendDocRequest = async () => {
    setSendingDocRequest(true);
    try {
      const response = await fetch(`${API_BASE_URL}/ui/claims/${claim.id}/send-deceased-doc-request`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (response.ok) {
        onMessage('Document request sent to executor / administrator');
        onSaveSuccess(result.claim);
      } else {
        onMessage(`Error: ${result.error || 'Failed to send request'}`);
      }
    } catch (err: any) {
      onMessage(`Error: ${err.message}`);
    } finally {
      setSendingDocRequest(false);
    }
  };

  const handleSendSigningLink = async () => {
    setSendingSigningLink(true);
    try {
      const response = await fetch(`${API_BASE_URL}/ui/claims/${claim.id}/send-deceased-signing-link`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (response.ok) {
        onMessage('Signing link sent to executor / administrator');
        onSaveSuccess(result.claim);
      } else {
        const miss = result.missing ? ` (missing: ${result.missing.join(', ')})` : '';
        onMessage(`Error: ${result.error || 'Failed to send signing link'}${miss}`);
      }
    } catch (err: any) {
      onMessage(`Error: ${err.message}`);
    } finally {
      setSendingSigningLink(false);
    }
  };

  const handleRequestExecutorId = async () => {
    const executorClientId = claim?.litigationFriendDetails?.executorClientId;
    if (!executorClientId) {
      onMessage('Error: Save executor details first before requesting ID');
      return;
    }
    setSendingIdRequest(true);
    try {
      const response = await fetch(`${API_BASE_URL}/ui/photo-id/send-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ clientIds: [executorClientId] })
      });
      const result = await response.json();
      if (response.ok && result.sent > 0) {
        onMessage('ID verification request sent to executor');
        setExecutorIdStatus('pending');
      } else {
        const errMsg = result.errors?.[0]?.error || result.error || 'Failed to send ID request';
        onMessage(`Error: ${errMsg}`);
      }
    } catch (err: any) {
      onMessage(`Error: ${err.message}`);
    } finally {
      setSendingIdRequest(false);
    }
  };

  const roleLabel = claim.litigationFriendDetails?.role === 'executor' || claim.litigationFriendDetails?.role === 'administrator'
    ? 'Executor/Administrator'
    : 'Litigation Friend';

  const isExecutorOrAdmin = formData.role === 'executor' || formData.role === 'administrator';

  return (
    <Modal onClose={onClose} contentClassName="lit-friend-modal-content">
      <div className="lit-friend">
        <div className="lit-friend__header">
          <h3>{claim.litigationFriendDetails ? roleLabel : 'Add Executor / Administrator / Litigation Friend'}</h3>
          <div className="lit-friend__header-meta">
            Claim: {claim.proclaimReference || claim.id} - {claim.firstName} {claim.lastName}
          </div>
        </div>

        <div className="lit-friend__body">
          {isEditMode ? (
            <div className="lit-friend__form">
              <div className="lit-friend__form-grid">
                <label>
                  First Name: <span className="required">*</span>
                  <input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} required />
                </label>
                <label>
                  Last Name: <span className="required">*</span>
                  <input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} required />
                </label>
                <label>
                  Email: <span className="required">*</span>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
                </label>
                <label>
                  Phone Number: <span className="required">*</span>
                  <input type="tel" value={formData.phoneNumber} onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})} required />
                </label>
                <label>
                  Date of Birth: <span className="required">*</span>
                  <input type="date" value={formData.dateOfBirth} onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})} required />
                </label>
                <label>
                  Address Line 1: <span className="required">*</span>
                  <input type="text" value={formData.addressLine1} onChange={(e) => setFormData({...formData, addressLine1: e.target.value})} required />
                </label>
                <label>
                  Address Line 2:
                  <input type="text" value={formData.addressLine2} onChange={(e) => setFormData({...formData, addressLine2: e.target.value})} />
                </label>
                <label>
                  Town: <span className="required">*</span>
                  <input type="text" value={formData.town} onChange={(e) => setFormData({...formData, town: e.target.value})} required />
                </label>
                <label>
                  County:
                  <input type="text" value={formData.county} onChange={(e) => setFormData({...formData, county: e.target.value})} />
                </label>
                <label>
                  Postcode: <span className="required">*</span>
                  <input type="text" value={formData.postcode} onChange={(e) => setFormData({...formData, postcode: e.target.value})} required />
                </label>
                <label>
                  Date Obtained:
                  <input type="date" value={formData.dateObtained} onChange={(e) => setFormData({...formData, dateObtained: e.target.value})} />
                </label>
                <label>
                  Method Obtained:
                  <input type="text" value={formData.methodObtained} onChange={(e) => setFormData({...formData, methodObtained: e.target.value})} />
                </label>
              </div>

              <label className="lit-friend__checkbox">
                <input type="checkbox" checked={formData.proofOnFile} onChange={(e) => setFormData({...formData, proofOnFile: e.target.checked})} />
                Proof on File
              </label>

              <label>
                Role: <span className="required">*</span>
                <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value as any, willStatus: '', grantOfProbateStatus: '', letterOfAdministrationStatus: '', deathCertificateStatus: ''})} required>
                  <option value="">Select Role</option>
                  <option value="litigationFriend">Litigation Friend</option>
                  <option value="executor">Executor</option>
                  <option value="administrator">Administrator</option>
                </select>
              </label>

              {isExecutorOrAdmin && (
                <div className="lit-friend__executor-fields">
                  <label>
                    Relationship to Client: <span className="required">*</span>
                    <input type="text" value={formData.relationshipToClient} onChange={(e) => setFormData({...formData, relationshipToClient: e.target.value})} placeholder="e.g. Spouse, Son, Daughter" required />
                  </label>
                  <label>
                    Death Certificate:
                    <select value={formData.deathCertificateStatus} onChange={(e) => setFormData({...formData, deathCertificateStatus: e.target.value as any})}>
                      <option value="">Select Status</option>
                      <option value="received">Received</option>
                      <option value="outstanding">Outstanding</option>
                    </select>
                  </label>
                  <label>
                    Date of Death:
                    <input type="date" value={formData.dateOfDeath} onChange={(e) => setFormData({...formData, dateOfDeath: e.target.value})} />
                  </label>
                  <label className="lit-friend__checkbox">
                    <input
                      type="checkbox"
                      checked={formData.willExists}
                      onChange={(e) => setFormData({
                        ...formData,
                        willExists: e.target.checked,
                        willStatus: e.target.checked ? formData.willStatus : 'na',
                        grantOfProbateStatus: e.target.checked ? formData.grantOfProbateStatus : '',
                        letterOfAdministrationStatus: e.target.checked ? '' : formData.letterOfAdministrationStatus,
                      })}
                    />
                    A will exists for the deceased
                  </label>
                  <label>
                    Will:
                    <select value={formData.willStatus} onChange={(e) => {
                      const val = e.target.value as 'received' | 'outstanding' | 'na' | '';
                      setFormData({
                        ...formData,
                        willStatus: val,
                        grantOfProbateStatus: val === 'na' ? '' : formData.grantOfProbateStatus,
                        letterOfAdministrationStatus: (val === 'received' || val === 'outstanding') ? '' : formData.letterOfAdministrationStatus
                      });
                    }}>
                      <option value="">Select Status</option>
                      <option value="received">Received</option>
                      <option value="outstanding">Outstanding</option>
                      <option value="na">N/A (Intestate)</option>
                    </select>
                  </label>

                  {(formData.willStatus === 'received' || formData.willStatus === 'outstanding') && (
                    <label>
                      Grant of Probate:
                      <select value={formData.grantOfProbateStatus} onChange={(e) => setFormData({...formData, grantOfProbateStatus: e.target.value as any})}>
                        <option value="">Select Status</option>
                        <option value="received">Received</option>
                        <option value="outstanding">Outstanding</option>
                        <option value="na">N/A</option>
                      </select>
                    </label>
                  )}

                  {formData.willStatus === 'na' && (
                    <label>
                      Letter of Administration:
                      <select value={formData.letterOfAdministrationStatus} onChange={(e) => setFormData({...formData, letterOfAdministrationStatus: e.target.value as any})}>
                        <option value="">Select Status</option>
                        <option value="received">Received</option>
                        <option value="outstanding">Outstanding</option>
                        <option value="na">N/A</option>
                      </select>
                    </label>
                  )}
                </div>
              )}

              <div className="lit-friend__actions">
                <button onClick={handleSave} className="lit-friend__btn lit-friend__btn--primary" disabled={saving}>
                  {saving ? 'Saving...' : (isExecutorOrAdmin ? 'Save Executor/Administrator' : 'Save Litigation Friend')}
                </button>
                <button onClick={handleCancel} className="lit-friend__btn lit-friend__btn--secondary">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="lit-friend__info">
              <InfoRow label="Name" value={`${claim.litigationFriendDetails?.firstName || ''} ${claim.litigationFriendDetails?.lastName || ''}`} />
              <InfoRow label="Email" value={claim.litigationFriendDetails?.email} />
              <InfoRow label="Phone" value={claim.litigationFriendDetails?.phoneNumber} />
              <InfoRow label="Date of Birth" value={claim.litigationFriendDetails?.dateOfBirth} />
              <InfoRow label="Address" value={`${claim.litigationFriendDetails?.addressLine1 || ''} ${claim.litigationFriendDetails?.addressLine2 || ''}`} />
              <InfoRow label="Town" value={claim.litigationFriendDetails?.town} />
              <InfoRow label="County" value={claim.litigationFriendDetails?.county} />
              <InfoRow label="Postcode" value={claim.litigationFriendDetails?.postcode} />
              <InfoRow label="Date Obtained" value={claim.litigationFriendDetails?.dateObtained} />
              <InfoRow label="Method Obtained" value={claim.litigationFriendDetails?.methodObtained} />
              <InfoRow label="Proof on File" value={claim.litigationFriendDetails?.proofOnFile ? 'Yes' : 'No'} />
              <InfoRow label="Role" value={claim.litigationFriendDetails?.role === 'executor' ? 'Executor' : claim.litigationFriendDetails?.role === 'administrator' ? 'Administrator' : claim.litigationFriendDetails?.role === 'litigationFriend' ? 'Litigation Friend' : ''} />

              {(claim.litigationFriendDetails?.role === 'executor' || claim.litigationFriendDetails?.role === 'administrator') && (
                <>
                  <InfoRow label="Relationship to Client" value={claim.litigationFriendDetails?.relationshipToClient} />
                  <InfoRow label="Death Certificate" value={claim.litigationFriendDetails?.deathCertificateStatus === 'received' ? 'Received' : claim.litigationFriendDetails?.deathCertificateStatus === 'outstanding' ? 'Outstanding' : ''} />
                  <InfoRow label="Will" value={claim.litigationFriendDetails?.willStatus === 'received' ? 'Received' : claim.litigationFriendDetails?.willStatus === 'outstanding' ? 'Outstanding' : claim.litigationFriendDetails?.willStatus === 'na' ? 'N/A (Intestate)' : ''} />
                  {(claim.litigationFriendDetails?.willStatus === 'received' || claim.litigationFriendDetails?.willStatus === 'outstanding') && (
                    <InfoRow label="Grant of Probate" value={claim.litigationFriendDetails?.grantOfProbateStatus === 'received' ? 'Received' : claim.litigationFriendDetails?.grantOfProbateStatus === 'outstanding' ? 'Outstanding' : claim.litigationFriendDetails?.grantOfProbateStatus === 'na' ? 'N/A' : ''} />
                  )}
                  {claim.litigationFriendDetails?.willStatus === 'na' && (
                    <InfoRow label="Letter of Administration" value={claim.litigationFriendDetails?.letterOfAdministrationStatus === 'received' ? 'Received' : claim.litigationFriendDetails?.letterOfAdministrationStatus === 'outstanding' ? 'Outstanding' : claim.litigationFriendDetails?.letterOfAdministrationStatus === 'na' ? 'N/A' : ''} />
                  )}
                </>
              )}

              <div className="lit-friend__actions">
                <button onClick={handleEdit} className="lit-friend__btn lit-friend__btn--primary">
                  {claim.litigationFriendDetails?.role === 'executor' || claim.litigationFriendDetails?.role === 'administrator' ? 'Edit Executor/Administrator' : 'Edit Litigation Friend'}
                </button>
              </div>

              {(claim.litigationFriendDetails?.role === 'executor' || claim.litigationFriendDetails?.role === 'administrator') && (
                <div className="lit-friend__deceased-docs">
                  <h5>Deceased Documents</h5>
                  {([
                    { kind: 'death_certificate', label: 'Death Certificate', urlField: 'deathCertDocUrl', show: true },
                    { kind: 'will', label: 'Will', urlField: 'willDocUrl', show: willExists },
                    { kind: 'grant_of_probate', label: 'Grant of Probate', urlField: 'grantOfProbateDocUrl', show: willExists },
                    { kind: 'letters_of_administration', label: 'Letters of Administration', urlField: 'lettersOfAdministrationDocUrl', show: !willExists },
                  ] as const).filter(d => d.show).map(d => (
                    <div key={d.kind} className="lit-friend__deceased-doc-row">
                      <span style={{ minWidth: 180, display: 'inline-block' }}>{d.label}:</span>
                      {lfd[d.urlField]
                        ? (
                          <button
                            type="button"
                            onClick={() => handleViewDoc(d.kind)}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                          >
                            View
                          </button>
                        )
                        : <span style={{ color: '#6b7280' }}>Not uploaded</span>}
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.docx"
                        disabled={uploadingKind === d.kind}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          // reset so re-selecting the same file re-fires onChange
                          e.target.value = '';
                          if (f) handleUploadDoc(d.kind, f);
                        }}
                        style={{ marginLeft: 12 }}
                      />
                    </div>
                  ))}

                  <div className="lit-friend__actions" style={{ marginTop: 12 }}>
                    <button
                      onClick={handleSendDocRequest}
                      className="lit-friend__btn lit-friend__btn--primary"
                      disabled={sendingDocRequest || !lfd.deathCertDocUrl}
                      title={!lfd.deathCertDocUrl ? 'Upload the death certificate first' : ''}
                    >
                      {sendingDocRequest ? 'Sending...' : 'Send to executor / administrator'}
                    </button>
                    <button
                      onClick={handleSendSigningLink}
                      className="lit-friend__btn lit-friend__btn--docuseal"
                      disabled={sendingSigningLink}
                    >
                      {sendingSigningLink ? 'Sending...' : 'Send NCBA + LOA signing link'}
                    </button>
                  </div>

                  {lfd.deceasedLoaSentAt && (
                    <div className="lit-friend__step-meta" style={{ marginTop: 8 }}>
                      Signing link sent {new Date(lfd.deceasedLoaSentAt).toLocaleString()}
                      {lfd.deceasedLoaSignedAt && ` · signed ${new Date(lfd.deceasedLoaSignedAt).toLocaleString()}`}
                    </div>
                  )}
                </div>
              )}

              {/* Executor ID Verification Section */}
              {claim.litigationFriendDetails?.executorClientId && (
                claim.litigationFriendDetails.role === 'executor' || claim.litigationFriendDetails.role === 'administrator'
              ) && (
                <div className="lit-friend__id-verification">
                  <h5>Executor ID Verification</h5>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                    {executorIdStatus === 'verified' ? (
                      <span style={{ color: '#16a34a', fontWeight: 600 }}>Verified</span>
                    ) : executorIdStatus === 'pending' || executorIdStatus === 'pending_review' ? (
                      <span style={{ color: '#d97706', fontWeight: 600 }}>Pending Review</span>
                    ) : executorIdStatus === 'rejected' ? (
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>Rejected</span>
                    ) : (
                      <span style={{ color: '#6b7280' }}>Not Requested</span>
                    )}
                    {executorIdStatus !== 'verified' && (
                      <button
                        onClick={handleRequestExecutorId}
                        className="lit-friend__btn lit-friend__btn--primary"
                        disabled={sendingIdRequest}
                        style={{ fontSize: '13px', padding: '6px 14px' }}
                      >
                        {sendingIdRequest ? 'Sending...' : (executorIdStatus ? 'Resend ID Request' : 'Request Executor ID')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LitigationFriendModal;
